// src/api/axiosInstance.js
import axios from 'axios';
import API_CONFIG from '../config/api';

// Use the API configuration for the base URL
const baseURL = API_CONFIG.BASE_URL;

// Enhanced axios instance with production settings
const axiosInstance = axios.create({
    baseURL: baseURL,
    timeout: API_CONFIG.REQUEST_CONFIG.timeout,
    headers: {
        ...API_CONFIG.REQUEST_CONFIG.headers,
        'X-Requested-With': 'XMLHttpRequest',
    },
    // Enable credentials for CORS
    withCredentials: true,
});

// 1. Add the token to every request
axiosInstance.interceptors.request.use(
    config => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 2. Enhanced response interceptor with retry logic and better error handling
axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async function (error) {
        const originalRequest = error.config;

        // Handle 401 Unauthorized - Token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem('refreshToken');

            if (refreshToken) {
                try {
                    // Use the API configuration for refresh URL
                    const rs = await axios.post(`${baseURL}${API_CONFIG.ENDPOINTS.REFRESH_TOKEN}`, {
                        refresh: refreshToken,
                    });
                    
                    const { access } = rs.data;
                    localStorage.setItem('accessToken', access);
                    axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + access;
                    
                    // Resend the original request with the new token
                    return axiosInstance(originalRequest);

                } catch (_error) {
                    // If refresh fails, log the user out
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('isAdmin');
                    window.location.href = '/login';
                    return Promise.reject(_error);
                }
            }
        }

        // Handle network errors with retry logic
        if (!error.response && error.request && !originalRequest._retry) {
            originalRequest._retry = true;
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Retry the request
            return axiosInstance(originalRequest);
        }

        // Log errors in development only
        if (process.env.NODE_ENV === 'development') {
            console.error('API Error:', error);
        }

        return Promise.reject(error);
    }
);

// 3. Updated login function
export const loginUser = async (username, password) => {
    try {
        // Use the API configuration for login endpoint
        const tokenResponse = await axiosInstance.post(API_CONFIG.ENDPOINTS.LOGIN, {
            username,
            password,
        });

        const { access, refresh } = tokenResponse.data;
        localStorage.setItem('accessToken', access);
        localStorage.setItem('refreshToken', refresh);
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${access}`;

        // Try to fetch user data to check if admin
        let isAdmin = false;
        try {
            // Use the API configuration for user info endpoint
            const meResponse = await axiosInstance.get(API_CONFIG.ENDPOINTS.USER_INFO);
            // Check for the new is_admin field first, then fall back to user.is_staff
            isAdmin = meResponse.data.is_admin || meResponse.data.user?.is_staff || meResponse.data.is_staff || false;
            console.log('User info:', meResponse.data);
        } catch (meError) {
            console.warn('User info endpoint not available, defaulting to non-admin:', meError.message);
        }
        
        localStorage.setItem('isAdmin', isAdmin);
        return { isAdmin };

    } catch (error) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('isAdmin');
        throw error;
    }
};

// 4. Helper function to get error message
export const getErrorMessage = (error) => {
    if (error.response) {
        const status = error.response.status;
        let message = '';
        
        switch (status) {
            case 401:
                message = API_CONFIG.ERROR_MESSAGES.UNAUTHORIZED;
                break;
            case 404:
                message = API_CONFIG.ERROR_MESSAGES.NOT_FOUND;
                break;
            case 400:
                message = API_CONFIG.ERROR_MESSAGES.VALIDATION_ERROR;
                break;
            case 500:
                message = API_CONFIG.ERROR_MESSAGES.SERVER_ERROR;
                break;
            default:
                message = API_CONFIG.ERROR_MESSAGES.SERVER_ERROR;
                break;
        }
        
        // Check if there's a specific error message in the response
        if (error.response.data) {
            if (typeof error.response.data === 'string') {
                message = error.response.data;
            } else if (error.response.data.message) {
                message = error.response.data.message;
            } else if (error.response.data.error) {
                message = error.response.data.error;
            } else if (error.response.data.detail) {
                message = error.response.data.detail;
            }
        }
        
        return message;
    } else if (error.request) {
        return API_CONFIG.ERROR_MESSAGES.NETWORK_ERROR;
    } else {
        return error.message || API_CONFIG.ERROR_MESSAGES.SERVER_ERROR;
    }
};

export default axiosInstance;
